import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from 'environments/environment';
import { FormatDecimal, FormatDecimalWithDot } from '@utils/formatDecimal';
import { PreferredCurrencyRates } from '../interfaces/CurrencyView';

/**
 * Maneja la "vista de moneda preferida": conversión SOLO visual de los precios.
 *
 * Selector binario en el header: "Valores originales" (modo real) vs la preferredCurrency.
 *
 * IMPORTANTE: este service NUNCA toca sessionStorage['currency'] (la moneda real que el
 * CurrencyService interceptor manda al back). Usa una clave propia: 'viewCurrency'.
 *
 * Estado de viewCurrency (string):
 *   ''            => modo "valores originales" (sin conversión).
 *   preferredCode => modo preferida (convierte montoReal / rateTo{monedaReal}).
 */
@Injectable({ providedIn: 'root' })
export class CurrencyViewService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  /** Clave NUEVA de storage. NO confundir con 'currency' (moneda real del interceptor). */
  static readonly STORAGE_KEY = 'viewCurrency';

  private readonly ratesUrl = `${this.baseUrl}/exchange-rates/rates`;

  // Estado reactivo
  readonly enabled = signal<boolean>(false);
  readonly preferredCurrency = signal<string>('');
  readonly rates = signal<PreferredCurrencyRates | null>(null);
  /** '' = valores originales; preferredCode = mostrando la preferida. */
  readonly viewCurrency = signal<string>('');

  /** Evita re-fetch en cada emisión de user$. */
  private initializedFor: string | null = null;

  /**
   * Inicializa la feature a partir de la moneda preferida que viene en /me.
   * Si no hay preferida => feature OFF y no se llama a ningún endpoint (cero regresión).
   */
  init(preferred: string | undefined | null): void {
    if (!preferred) {
      this.reset();
      this.initializedFor = '';
      return;
    }
    if (this.initializedFor === preferred) return;
    this.initializedFor = preferred;

    this.enabled.set(true);
    this.preferredCurrency.set(preferred);

    const params = new HttpParams().set('currency', preferred);
    this.http
      .get<PreferredCurrencyRates>(this.ratesUrl, { params })
      .subscribe({
        next: (rates) => {
          this.rates.set(rates ?? null);
          // Default = preferida, salvo que el usuario ya haya elegido un modo válido.
          const persisted = sessionStorage.getItem(CurrencyViewService.STORAGE_KEY);
          const valid = persisted === '' || persisted === preferred;
          this.viewCurrency.set(valid ? (persisted as string) : preferred);
        },
        error: () => {
          // Degradar a valores originales si las tasas fallan; nunca romper la vista.
          this.reset();
        },
      });
  }

  /** Cambia el modo: '' = valores originales, o el code de la preferida. */
  setViewCurrency(code: string): void {
    this.viewCurrency.set(code);
    sessionStorage.setItem(CurrencyViewService.STORAGE_KEY, code);
  }

  /** True si se está mostrando la moneda real (modo originales / feature off). */
  isReal(realCurrency: string): boolean {
    const view = this.viewCurrency();
    return !this.enabled() || view === '' || view === realCurrency;
  }

  /**
   * Convierte un monto de la moneda real a la preferida. Passthrough si no aplica.
   * @param amount número o string formateado ("1.234,56")
   * @param realCurrency moneda real del dato (ARS/USD)
   */
  convert(amount: number | string, realCurrency: string): number {
    const num =
      typeof amount === 'string' ? parseFloat(FormatDecimalWithDot(amount)) : amount;
    if (!num || isNaN(num)) return 0;
    if (this.isReal(realCurrency)) return num;

    // El back da cuánto vale 1 preferida en la moneda real => para ir real→preferida se DIVIDE.
    const rates = this.rates();
    const divisor =
      realCurrency === 'ARS'
        ? rates?.rateToARS
        : realCurrency === 'USD'
          ? rates?.rateToUSD
          : undefined;
    return divisor ? num / divisor : num; // sin rate => passthrough
  }

  /**
   * Inverso de convert: pasa un monto de la moneda preferida (lo que el usuario tipea) a la
   * moneda real (lo que viaja al back). Passthrough si no aplica.
   * @param amount número o string formateado ("1.234,56") en moneda preferida
   * @param realCurrency moneda real del dato (ARS/USD)
   */
  convertToReal(amount: number | string, realCurrency: string): number {
    const num =
      typeof amount === 'string' ? parseFloat(FormatDecimalWithDot(amount)) : amount;
    if (!num || isNaN(num)) return 0;
    if (this.isReal(realCurrency)) return num;

    const rates = this.rates();
    const factor =
      realCurrency === 'ARS'
        ? rates?.rateToARS
        : realCurrency === 'USD'
          ? rates?.rateToUSD
          : undefined;
    return factor ? num * factor : num; // sin rate => passthrough
  }

  /** Devuelve el string listo para mostrar: "MXN 1.234,56". */
  format(amount: number | string, realCurrency: string): string {
    const code = this.isReal(realCurrency) ? realCurrency : this.viewCurrency();
    return `${code} ${FormatDecimal(this.convert(amount, realCurrency))}`;
  }

  /** Limpia el estado y la clave de storage (logout / cambio de agencia / fallo de tasas). */
  reset(): void {
    this.enabled.set(false);
    this.preferredCurrency.set('');
    this.rates.set(null);
    this.viewCurrency.set('');
    this.initializedFor = null;
    sessionStorage.removeItem(CurrencyViewService.STORAGE_KEY);
  }
}
